import { isNil, isNotNil } from "@mcwd/typescript-type-guards";
import type { GateLevelSource } from "../../types/definitions/gating/gating-level-source.js";
import { MarketoFormSettingsObject, type AllowedGatingStatesForMktoForm, type MarketoFormSettingsKey } from "../marketo-form/marketo-form-setting.js";
import type { AnyFormSettingsKey } from "./form-settings-keys.js";

type AllowedGatingStates_w_NoGate<TMktoForm extends MarketoFormSettingsKey> = AllowedGatingStatesForMktoForm<TMktoForm> | "NoGate";

function getDefaultGateLevelFromAllowedGatingStates<
  const TMktoForm extends MarketoFormSettingsKey,
  const TGateLevelSources extends readonly [GateLevelSource, ...GateLevelSource[]],
  const TAllowedGatingStates extends readonly [AllowedGatingStates_w_NoGate<TMktoForm>, ...AllowedGatingStates_w_NoGate<TMktoForm>[]],
>({
  formSettingsKey,
  mktoKey: _mktoKey,
  gateLevelSource,
  allowedGatingStates
}: {
  formSettingsKey: AnyFormSettingsKey,
  mktoKey: TMktoForm,
  gateLevelSource: TGateLevelSources,
  allowedGatingStates: TAllowedGatingStates
}) {
  const glsLen = gateLevelSource.length;
  const gls0 = gateLevelSource[0];
  const agsLen = allowedGatingStates.length;
  if (glsLen === 1 && gls0 === "Self") {
    if (agsLen > 2) {
      throw new Error(`There cannot be more than 2 values in the 'allowedGatingStates' array when the 'gateLevelSource' property array contains only 'Self'. If more gating states have been added this logic may need to be updated.`);
    }
    const requiredStateIdx = allowedGatingStates.findIndex(a => (a === "FullGate" || a === "PartialGate"));
    const ags0 = allowedGatingStates[0];
    const ags1 = allowedGatingStates[1];
    if (requiredStateIdx === 0) {
      if (agsLen === 2 && ags1 !== "BlindSubmit") {
        throw new Error(`Invalid 'allowedGatingStates' setting for formSettingsKey '${formSettingsKey}'. allowedGatingStates[1] contains an invalid gating state value '${ags1}' that cannot happen due to the 'gateLevelSource' property value of ['Self'] which allows only a single default GatingLevel`);
      }
      return ags0 as "FullGate" | "PartialGate";
    }
    else if (requiredStateIdx === 1) {
      if (agsLen === 2 && ags0 !== "BlindSubmit") {
        throw new Error(`Invalid 'allowedGatingStates' setting for formSettingsKey '${formSettingsKey}'. allowedGatingStates[0] contains an invalid gating state value '${ags0}' that cannot happen due to the 'gateLevelSource' property value of ['Self'] which allows only a single default GatingLevel`);
      }
      return ags1 as "FullGate" | "PartialGate";
    }
    else if (requiredStateIdx < 0) {
      if (agsLen === 1) {
        throw new Error(`Invalid 'allowedGatingStates' setting for formSettingsKey '${formSettingsKey}' with only one gating type specified '${ags0}' which is invalid as a default value when 'gateLevelSource' is set to ['Self']`);
      }
      else {
        throw new Error(`Invalid 'allowedGatingStates' setting for formSettingsKey '${formSettingsKey}' with value ['${ags0}', '${ags1}'] which are both invalid as a default value when 'gateLevelSource' is set to ['Self']`);
      }
    }
  }
  return null;
}

export function createExperienceSettings<
  const TFormExp extends AnyFormSettingsKey,
  const TMktoForm extends MarketoFormSettingsKey,
  const TFormTrackingName extends string,
  const TCanUseModal extends boolean,
  const TGateLevelSources extends readonly ["Self"],
  const TAllowedGatingStates extends readonly AllowedGatingStatesForMktoForm<TMktoForm>[]
  & (
    | (readonly [AllowedGatingStatesForMktoForm<TMktoForm>, AllowedGatingStatesForMktoForm<TMktoForm>] & (readonly ("FullGate" | "BlindSubmit")[] | readonly ("PartialGate" | "BlindSubmit")[]))
    | (readonly [AllowedGatingStatesForMktoForm<TMktoForm>] & (readonly ["FullGate"] | readonly ["PartialGate"])) 
  )
>({
  formSettingsKey,
  mktoKey,
  formTrackingName,
  canUseModal,
  gateLevelSource,
  allowedGatingStates
}: {
  formSettingsKey: TFormExp,
  mktoKey: TMktoForm,
  formTrackingName: TFormTrackingName,
  canUseModal: TCanUseModal,
  gateLevelSource: TGateLevelSources,
  allowedGatingStates: TAllowedGatingStates
}): MarketoFormSettingsObject[TMktoForm] & {
  readonly formSettingsKey: TFormExp,
  readonly formTrackingName: TFormTrackingName,
  readonly canUseModal: TCanUseModal,
  readonly gateLevelSource: TGateLevelSources,
  readonly allowedGatingStates: TAllowedGatingStates,
  readonly defaultGateLevel: Exclude<TAllowedGatingStates[number], "BlindSubmit">
}

export function createExperienceSettings<
  const TFormExp extends AnyFormSettingsKey,
  const TMktoForm extends MarketoFormSettingsKey,
  const TFormTrackingName extends string,
  const TCanUseModal extends boolean,
  const TGateLevelSources extends readonly ["Self", Exclude<GateLevelSource, "Self">, ...Exclude<GateLevelSource, "Self">[]],
  const TAllowedGatingStates extends readonly AllowedGatingStates_w_NoGate<TMktoForm>[]
    & readonly [AllowedGatingStates_w_NoGate<TMktoForm>, AllowedGatingStates_w_NoGate<TMktoForm>, ...AllowedGatingStates_w_NoGate<TMktoForm>[]],
  const TDefaultGateLevel extends TAllowedGatingStates[number] & ("FullGate" | "PartialGate")
>({
  formSettingsKey,
  mktoKey,
  formTrackingName,
  canUseModal,
  gateLevelSource,
  allowedGatingStates,
  defaultGateLevel
}: {
  formSettingsKey: TFormExp,
  mktoKey: TMktoForm,
  formTrackingName: TFormTrackingName,
  canUseModal: TCanUseModal,
  gateLevelSource: TGateLevelSources,
  allowedGatingStates: TAllowedGatingStates,
  defaultGateLevel: TDefaultGateLevel
}): MarketoFormSettingsObject[TMktoForm] & {
  readonly formSettingsKey: TFormExp,
  readonly formTrackingName: TFormTrackingName,
  readonly canUseModal: TCanUseModal,
  readonly gateLevelSource: TGateLevelSources,
  readonly allowedGatingStates: TAllowedGatingStates,
  readonly defaultGateLevel: TDefaultGateLevel
}

export function createExperienceSettings<
  const TFormExp extends AnyFormSettingsKey,
  const TMktoForm extends MarketoFormSettingsKey,
  const TFormTrackingName extends string,
  const TCanUseModal extends boolean,
  const TGateLevelSources extends (readonly [Exclude<GateLevelSource, "Self">]) | (readonly [Exclude<GateLevelSource, "Self">, Exclude<GateLevelSource, "Self">, ...Exclude<GateLevelSource, "Self">[]]),
  const TAllowedGatingStates extends readonly AllowedGatingStates_w_NoGate<TMktoForm>[]
    & readonly [AllowedGatingStates_w_NoGate<TMktoForm>, AllowedGatingStates_w_NoGate<TMktoForm>, ...AllowedGatingStates_w_NoGate<TMktoForm>[]],
>({
  formSettingsKey,
  mktoKey,
  formTrackingName,
  canUseModal,
  gateLevelSource,
  allowedGatingStates
}: {
  formSettingsKey: TFormExp,
  mktoKey: TMktoForm,
  formTrackingName: TFormTrackingName,
  canUseModal: TCanUseModal,
  gateLevelSource: TGateLevelSources,
  allowedGatingStates: TAllowedGatingStates,
}): MarketoFormSettingsObject[TMktoForm] & {
  readonly formSettingsKey: TFormExp,
  readonly formTrackingName: TFormTrackingName,
  readonly canUseModal: TCanUseModal,
  readonly gateLevelSource: TGateLevelSources,
  readonly allowedGatingStates: TAllowedGatingStates,
}

export function createExperienceSettings<
  const TFormExp extends AnyFormSettingsKey,
  const TMktoForm extends MarketoFormSettingsKey,
  const TFormTrackingName extends string,
  const TCanUseModal extends boolean,
  const TGateLevelSources extends readonly [GateLevelSource, ...GateLevelSource[]],
  const TAllowedGatingStates extends readonly [AllowedGatingStates_w_NoGate<TMktoForm>, ...AllowedGatingStates_w_NoGate<TMktoForm>[]],
>({
  formSettingsKey,
  mktoKey,
  formTrackingName,
  canUseModal,
  gateLevelSource,
  allowedGatingStates,
  defaultGateLevel
}: MarketoFormSettingsObject[TMktoForm] & {
  readonly formSettingsKey: TFormExp,
  readonly mktoKey: TMktoForm,
  readonly formTrackingName: TFormTrackingName,
  readonly canUseModal: TCanUseModal,
  readonly gateLevelSource: [...TGateLevelSources],
  readonly allowedGatingStates: TAllowedGatingStates,
  readonly defaultGateLevel?: TAllowedGatingStates[number],
}) {
  if (gateLevelSource.length === 0) {
    throw new Error(`gateLevelSource is not allowed to be of length 0. formSettingsKey: '${formSettingsKey}'`);
  }
  if (allowedGatingStates.length === 0) {
    throw new Error(`allowedGatingStates is not allowed to be of length 0. formSettingsKey: '${formSettingsKey}'`);
  }
  
  let defaultGateLevelObj: { defaultGateLevel: TAllowedGatingStates[number] } | Record<string, never> = {};
  if (isNil(defaultGateLevel)) {
    const gateLevelForSelfSourceFromAllowedGatingStates = getDefaultGateLevelFromAllowedGatingStates({ allowedGatingStates, formSettingsKey, gateLevelSource, mktoKey });
    if (isNotNil(gateLevelForSelfSourceFromAllowedGatingStates)) {
      defaultGateLevelObj = { defaultGateLevel: gateLevelForSelfSourceFromAllowedGatingStates };
    }
  }
  else {
    defaultGateLevelObj = { defaultGateLevel };
  }

  if ("defaultGateLevel" in defaultGateLevelObj) {
    if (allowedGatingStates.includes(defaultGateLevelObj.defaultGateLevel) === false) {
      throw new Error(`The value for property 'defaultGateLevel' must be contained in the 'allowedGatingStates' array property.`);
    }
    if (defaultGateLevelObj.defaultGateLevel !== "FullGate" && defaultGateLevelObj.defaultGateLevel !== "PartialGate") {
      throw new Error(`The value for property 'defaultGateLevel' must be either "FullGate" or "PartialGate"`);
    }
  }
  else if (gateLevelSource.includes("Self")) {
    throw new Error(`Property 'defaultGateLevel' is required when providing more than one 'gateLevelSource' item where one of those values is 'Self'`);
  }

  const expSettings = {
    formSettingsKey: formSettingsKey,
    formTrackingName,
    canUseModal,
    gateLevelSource,
    allowedGatingStates,
    ...defaultGateLevelObj,
    ...MarketoFormSettingsObject[mktoKey],
  } as const;
  Object.freeze(expSettings);
  return expSettings;
}